import { useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Ban,
  MoreVertical,
  RotateCcw,
  Send,
  Settings2,
  Trash2,
  UserPlus,
  XCircle,
} from "lucide-react";
import { useTeamMembers, type TeamMember } from "@/hooks/use-team-members";
import { useWorkspaceInvites, type WorkspaceInvite } from "@/hooks/use-workspace-invites";
import { useWorkspace } from "@/hooks/use-workspace";
import { useAuth } from "@/hooks/use-auth";
import { InviteMemberDialog } from "@/components/workspace/InviteMemberDialog";
import { MemberPermissionsDialog } from "@/components/workspace/MemberPermissionsDialog";

function initials(name: string | null, email: string | null): string {
  if (name) return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  return (email?.[0] ?? "?").toUpperCase();
}

function roleLabel(role: TeamMember["role"]) {
  return role === "owner" || role === "admin" ? "Admin" : "Team Member";
}

function MemberRow({
  member,
  isSelf,
  canManage,
  onPermissions,
  onDisable,
  onReactivate,
  onDelete,
}: {
  member: TeamMember;
  isSelf: boolean;
  canManage: boolean;
  onPermissions: () => void;
  onDisable: () => void;
  onReactivate: () => void;
  onDelete: () => void;
}) {
  const isOwner = member.role === "owner";
  const isDisabled = member.status === "inactive";
  const showActions = canManage && !isSelf && !isOwner;

  return (
    <div className="flex items-center gap-3 py-3">
      <Avatar className="h-9 w-9 shrink-0">
        <AvatarFallback className="text-xs bg-muted">
          {initials(member.full_name, member.email)}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium leading-none truncate">
          {member.full_name ?? member.email ?? "Unknown"}
          {isSelf && <span className="ml-1.5 text-xs text-muted-foreground font-normal">(you)</span>}
        </p>
        {member.full_name && member.email && (
          <p className="text-xs text-muted-foreground mt-0.5 truncate">{member.email}</p>
        )}
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <Badge variant={isOwner || member.role === "admin" ? "default" : "secondary"}>
          {roleLabel(member.role)}{isOwner && " · Owner"}
        </Badge>
        {isDisabled && (
          <Badge variant="outline" className="border-destructive/40 text-destructive">
            Disabled
          </Badge>
        )}
        {canManage && member.role === "member" && !isDisabled && (
          <Button variant="ghost" size="sm" className="h-8 text-xs gap-1" onClick={onPermissions}>
            <Settings2 className="h-3.5 w-3.5" />
            Permissions
          </Button>
        )}
        {showActions && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-muted-foreground"
                aria-label="Member actions"
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              {isDisabled ? (
                <>
                  <DropdownMenuItem onClick={onReactivate}>
                    <RotateCcw className="mr-2 h-4 w-4" />
                    Reactivate
                  </DropdownMenuItem>
                  <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={onDelete}>
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </DropdownMenuItem>
                </>
              ) : (
                <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={onDisable}>
                  <Ban className="mr-2 h-4 w-4" />
                  Disable Account
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
}

function PendingInviteRow({
  invite,
  onResend,
  onCancel,
  resendDisabled,
}: {
  invite: WorkspaceInvite;
  onResend: () => void;
  onCancel: () => void;
  resendDisabled: boolean;
}) {
  return (
    <div className="flex items-center gap-3 py-3">
      <Avatar className="h-9 w-9 shrink-0">
        <AvatarFallback className="text-xs bg-muted text-muted-foreground">
          {invite.email[0].toUpperCase()}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium leading-none truncate">{invite.email}</p>
        <p className="text-xs text-muted-foreground mt-0.5">
          Sent {formatDistanceToNow(new Date(invite.created_at), { addSuffix: true })}
        </p>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <Badge variant="outline" className="capitalize">{invite.role}</Badge>
        <Badge variant="secondary">Pending</Badge>
        <Button
          variant="outline"
          size="sm"
          className="h-8 text-xs gap-1"
          onClick={onResend}
          disabled={resendDisabled}
        >
          <Send className="h-3.5 w-3.5" />
          Resend Invite
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-muted-foreground"
              aria-label="Invite actions"
            >
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={onCancel}>
              <XCircle className="mr-2 h-4 w-4" />
              Cancel Invite
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

export function TeamTab() {
  const { user } = useAuth();
  const { isAdmin } = useWorkspace();
  const {
    members: allMembers,
    isLoading,
    removeMember,
    disableMember,
    reactivateMember,
  } = useTeamMembers();
  const { invites, isLoading: invitesLoading, revokeInvite, resendInvite } = useWorkspaceInvites();

  const visibleMembers = allMembers.filter(m => m.role !== "owner");
  const activeMembers = visibleMembers.filter(m => m.status === "active");
  const disabledMembers = visibleMembers.filter(m => m.status === "inactive");

  const [inviteOpen, setInviteOpen] = useState(false);
  const [permTarget, setPermTarget] = useState<TeamMember | null>(null);
  const [disableTarget, setDisableTarget] = useState<TeamMember | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TeamMember | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<WorkspaceInvite | null>(null);

  const renderMember = (m: TeamMember) => (
    <MemberRow
      key={m.id}
      member={m}
      isSelf={m.user_id === user?.id}
      canManage={isAdmin}
      onPermissions={() => setPermTarget(m)}
      onDisable={() => setDisableTarget(m)}
      onReactivate={() => reactivateMember.mutate(m.id)}
      onDelete={() => setDeleteTarget(m)}
    />
  );

  const targetName = (m: TeamMember | null) =>
    m?.full_name ?? m?.email ?? "this user";

  return (
    <>
      <Card className="shadow-soft">
        <CardHeader className="flex flex-row items-start justify-between space-y-0">
          <div>
            <CardTitle>Team Members</CardTitle>
            <CardDescription className="mt-1">
              Manage who has access to your workspace.
            </CardDescription>
          </div>
          {isAdmin && (
            <Button size="sm" onClick={() => setInviteOpen(true)}>
              <UserPlus className="h-4 w-4 mr-2" />
              Invite
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2].map(i => <Skeleton key={i} className="h-14 w-full rounded-md" />)}
            </div>
          ) : activeMembers.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">No active members.</p>
          ) : (
            <div className="divide-y divide-border">
              {activeMembers.map(renderMember)}
            </div>
          )}
        </CardContent>
      </Card>

      {isAdmin && (invitesLoading || invites.length > 0) && (
        <Card className="shadow-soft">
          <CardHeader>
            <CardTitle className="text-base">Pending Invites</CardTitle>
          </CardHeader>
          <CardContent>
            {invitesLoading ? (
              <Skeleton className="h-14 w-full rounded-md" />
            ) : (
              <div className="divide-y divide-border">
                {invites.map(inv => (
                  <PendingInviteRow
                    key={inv.id}
                    invite={inv}
                    onResend={() =>
                      resendInvite.mutate({
                        email: inv.email,
                        role: inv.role === "admin" ? "admin" : "member",
                      })
                    }
                    onCancel={() => setRevokeTarget(inv)}
                    resendDisabled={resendInvite.isPending}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {isAdmin && disabledMembers.length > 0 && (
        <Card className="shadow-soft">
          <CardHeader>
            <CardTitle className="text-base">Disabled Members</CardTitle>
            <CardDescription className="mt-1">
              These users cannot access the workspace. Reactivate to restore access, or delete to remove them permanently.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="divide-y divide-border">
              {disabledMembers.map(renderMember)}
            </div>
          </CardContent>
        </Card>
      )}

      <InviteMemberDialog open={inviteOpen} onOpenChange={setInviteOpen} />

      {permTarget && (
        <MemberPermissionsDialog
          member={permTarget}
          open
          onOpenChange={open => { if (!open) setPermTarget(null); }}
        />
      )}

      <AlertDialog
        open={!!disableTarget}
        onOpenChange={open => { if (!open) setDisableTarget(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disable account?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to disable {targetName(disableTarget)}'s account?
              This will prevent them from accessing the workspace until you reactivate them.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (disableTarget) disableMember.mutate(disableTarget.id);
                setDisableTarget(null);
              }}
            >
              Disable
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={open => { if (!open) setDeleteTarget(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete account?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to <span className="font-semibold text-destructive">permanently delete</span> {targetName(deleteTarget)}&apos;s account?
              This action cannot be undone and will remove all associated workspace data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (deleteTarget) removeMember.mutate(deleteTarget.id);
                setDeleteTarget(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!revokeTarget}
        onOpenChange={open => { if (!open) setRevokeTarget(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel invite?</AlertDialogTitle>
            <AlertDialogDescription>
              The invite sent to {revokeTarget?.email} will be cancelled. They will not be able
              to use the invite link.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep invite</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (revokeTarget) revokeInvite.mutate(revokeTarget.id);
                setRevokeTarget(null);
              }}
            >
              Cancel Invite
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
